import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PuzzleBackground.css';
import './PuzzleBackgroundMotion.css';

const PUZZLE_PATH =
  'M30,5 L42,5 C42,5 44,-4 50,-4 C56,-4 58,5 58,5 L70,5 ' +
  'C78,5 84,11 84,19 L84,31 C84,31 93,33 93,43 C93,53 84,55 84,55 ' +
  'L84,67 C84,75 78,81 70,81 L58,81 C58,81 56,90 50,90 C44,90 42,81 42,81 ' +
  'L30,81 C22,81 16,75 16,67 L16,55 C16,55 7,53 7,43 C7,33 16,31 16,31 ' +
  'L16,19 C16,11 22,5 30,5 Z';

const basePieces = [
  // Keep the vertical lanes you already like (top %). Horizontal movement is handled by animation.
  { color: '#81C784', size: 90, top: '3%', rotate: -20, opacity: 0.65 },
  { color: '#64B5F6', size: 70, top: '15%', rotate: 40, opacity: 0.55 },
  { color: '#FFB74D', size: 80, top: '0%', rotate: 18, opacity: 0.5 },
  { color: '#CE93D8', size: 60, top: '28%', rotate: -55, opacity: 0.45 },
  { color: '#EF9A9A', size: 72, top: '20%', rotate: 130, opacity: 0.45 },
  { color: '#A5D6A7', size: 55, top: '38%', rotate: 75, opacity: 0.4 },
];

const randBetween = (min, max) => min + Math.random() * (max - min);

const makeId = () =>
  `puz_${Date.now().toString(36)}_${Math.random().toString(16).slice(2)}`;

const pickBasePiece = () => basePieces[Math.floor(Math.random() * basePieces.length)];

const makeOnePiece = (basePiece, { spreadAcrossScreen } = { spreadAcrossScreen: true }) => {
  const travelDuration = Math.round(randBetween(38, 70)); // seconds (slower = larger)
  const spinDuration = Math.round(randBetween(14, 28)); // seconds
  const delay = spreadAcrossScreen ? -Math.round(randBetween(0, travelDuration)) : 0;
  const xOffset = Math.round(randBetween(0, 35)); // px, prevents “in a straight line” feel

  return {
    id: makeId(),
    ...basePiece,
    travelDuration,
    spinDuration,
    delay,
    xOffset,
    isPoofing: false,
  };
};

const makeInitialPieces = () => basePieces.map((p) => makeOnePiece(p, { spreadAcrossScreen: true }));

const PuzzleBackground = () => {
  const [pieces, setPieces] = useState(() => makeInitialPieces());
  const timeoutsRef = useRef(new Set());

  useEffect(() => {
    return () => {
      for (const t of timeoutsRef.current) clearTimeout(t);
      timeoutsRef.current.clear();
    };
  }, []);

  const poofPiece = useCallback((id) => {
    let started = false;

    setPieces((prev) => {
      const next = prev.map((p) => {
        if (p.id !== id) return p;
        if (p.isPoofing) return p;
        started = true;
        return { ...p, isPoofing: true };
      });
      return next;
    });

    if (!started) return;

    // Match the CSS poof animation length.
    const t = setTimeout(() => {
      setPieces((prev) => {
        const remaining = prev.filter((p) => p.id !== id);
        const replacement = makeOnePiece(pickBasePiece(), { spreadAcrossScreen: false });
        return [...remaining, replacement];
      });
      timeoutsRef.current.delete(t);
    }, 260);

    timeoutsRef.current.add(t);
  }, []);

  const handlePiecePointerDown = useCallback((event, id) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    poofPiece(id);
  }, [poofPiece]);

  const handlePieceClick = useCallback((event, id) => {
    event.stopPropagation();
    poofPiece(id);
  }, [poofPiece]);

  return (
    <div className="puzzle-bg" aria-hidden="true">
      {pieces.map((p) => (
        <div
          key={p.id}
          className={`puzzle-bg-piece-wrapper${p.isPoofing ? ' is-poofing' : ''}`}
          style={{
            top: p.top,
            opacity: p.opacity,
            '--puzzle-travel-duration': `${p.travelDuration}s`,
            '--puzzle-delay': `${p.delay}s`,
            '--puzzle-x-offset': `${p.xOffset}px`,
          }}
          onPointerDown={(e) => handlePiecePointerDown(e, p.id)}
          onClick={(e) => handlePieceClick(e, p.id)}
        >
          <svg
            className={`puzzle-bg-piece${p.isPoofing ? ' is-poofing' : ''}`}
            viewBox="0 -8 100 104"
            width={p.size}
            height={p.size}
            style={{
              '--puzzle-rot': `${p.rotate}deg`,
              '--puzzle-spin-duration': `${p.spinDuration}s`,
            }}
          >
            <path d={PUZZLE_PATH} fill={p.color} />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default PuzzleBackground;
